import {
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import { v4 as uuidV4 } from "uuid";

import {
    AnalyticsEventPayload,
    AnalyticsListener,
    UIAnalyticsEvent
} from "@atlaskit/analytics-next";

import { Cookie } from "@/utils/cookie";
import { checkFeatureFlagGate } from "@/utils/feature-flags";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";
import { useOneTrustContext } from "@/utils/onetrust-provider";

import { Tracking } from "../atl-analytics/tracking";

import { useAnalyticsClient } from "./analytics-client";

type QueuedEvent = {
    payload: UIAnalyticsEvent["payload"];
    context: UIAnalyticsEvent["context"];
    channel?: string;
};

export const WacAnalyticsListener = ({ children }: PropsWithChildren) => {
    const analyticsClient = useAnalyticsClient();
    const { analyticsOptOut, isConsentLoaded } = useOneTrustContext();
    const [loginUserDetected, setLoginUserDetected] = useState("false");
    const queueRef = useRef<QueuedEvent[]>([]);
    const traceIdRef = useRef<string | null>(null);

    const isEnableOperationalEventWhenOptedOut = checkFeatureFlagGate(
        featureFlags.gwp10416DisableNonOperationalAnalyticsGateUip
    );

    const isReady = isEnableOperationalEventWhenOptedOut
        ? Boolean(analyticsClient && isConsentLoaded)
        : Boolean(analyticsClient);

    useEffect(() => {
        const isVoltronMultiTouchEnabled = checkFeatureFlagGate(
            featureFlags.voltronMultiTouchAnalytics
        );
        if (isVoltronMultiTouchEnabled) {
            const generatedTraceId = uuidV4();
            traceIdRef.current = generatedTraceId;
            localStorage.setItem("trace_id", generatedTraceId);
        }
    }, []);

    useEffect(() => {
        const cookieValue = Cookie.readCookie("login_user_detected");
        if (cookieValue) {
            setLoginUserDetected(cookieValue);
        }
    }, []);

    const flush = useCallback(
        (
            payload: UIAnalyticsEvent["payload"],
            context: UIAnalyticsEvent["context"],
            channel?: string
        ) => {
            if (!analyticsClient) return;

            if (
                isEnableOperationalEventWhenOptedOut &&
                analyticsOptOut &&
                payload?.["eventType"] !== "operational"
            ) {
                return;
            }

            const traceid = traceIdRef.current;

            if (channel === "signup") {
                const analyticsDataList = [...context, payload];
                const analyticsEventMagnolia = analyticsDataList.reduce(
                    (mergedAnalyticsEvent, currentAnalyicsEvent) => {
                        return {
                            ...mergedAnalyticsEvent,
                            ...currentAnalyicsEvent,
                            ...(traceid ? { trace_id: traceid } : {})
                        };
                    },
                    {}
                );
                Tracking.trackEvent(
                    analyticsEventMagnolia["eventName"],
                    analyticsEventMagnolia
                );
                return;
            }

            const processedEventPayload: AnalyticsEventPayload = {
                actionSubject: "component",
                eventType: "ui",
                source: "default",
                ...payload,
                attributes: {
                    ...(payload?.["attributes"] || {}),
                    loginUserDetected,
                    ...(traceid ? { trace_id: traceid } : {})
                }
            };
            if (!!window?.cmsPageVariant) {
                processedEventPayload["attributes"].cmsPageVariant =
                    window.cmsPageVariant;
            }

            const ANALYTICS_TYPE = process.env["ANALYTICS_TYPE"] || "console";
            if (ANALYTICS_TYPE === "console") {
                console.log({ event: processedEventPayload });
            }

            void analyticsClient.onEvent(null, processedEventPayload);
        },
        [analyticsClient, analyticsOptOut, loginUserDetected]
    );

    useEffect(() => {
        if (!isReady || queueRef.current.length === 0) return;
        const q = queueRef.current;
        queueRef.current = [];
        q.forEach(({ payload, context, channel }) =>
            flush(payload, context, channel)
        );
    }, [isReady, flush]);

    const onEvent = useCallback(
        (event: UIAnalyticsEvent, channel?: string | undefined) => {
            if (!isReady) {
                queueRef.current.push({
                    payload: event.payload,
                    context: event.context,
                    channel
                });
                return;
            }
            flush(event.payload, event.context, channel);
        },
        [isReady, flush]
    );

    return (
        /**
         * Current channels are:
         * - atl-global: For all auto-generated events from InitializeAutoAtlAnalytics
         * - marketing: For events generated via the React components themselves
         *
         * TODO - can add in the FabricEventsListeners to catch atlasKit and fabric components etc.
         */
        <AnalyticsListener channel={"*"} onEvent={onEvent}>
            {children}
        </AnalyticsListener>
    );
};
